import _ from 'lodash';
import React from 'react';
import ReactTooltip from 'react-tooltip';

import Table from 'corp/components/UI/Table';
import TimeFormatter from 'corp/components/Format/TimeFormatter';
import BooleanFormatter from 'corp/components/Format/BooleanFormatter';
import ObjectIDFormatter from 'corp/components/Format/ObjectIDFormatter';
import CodeFormatter from 'corp/components/Format/CodeFormatter';
import RightAlignedFormatter from 'corp/components/Format/RightAlignedFormatter';
import { calculateQualityScores } from '@scale/shared/document/util';
import { getLidarLiteLink } from 'react-scale/lib/helpers/redirectHelpers';

interface ColumnProps {
  scalerAccess: boolean;
  isLidar?: boolean;
  isDocumentModel?: boolean;
}

const diffViewerLinkFormatter = (value: $FIXME, isLidar: $FIXME, original: $FIXME) => {
  return (
    <pre>
      <div>
        <a href={`/corp/lookup/${value}`} target="_blank" rel="noopener noreferrer">
          Audit Page
        </a>

        {isLidar && original?.result === 'fixed' && (
          <React.Fragment>
            |
            <a
              href={getLidarLiteLink({ auditId: value, entrypoint: 'diff-viewer' })}
              target="_blank"
              rel="noopener noreferrer"
            >
              Diff View
            </a>
          </React.Fragment>
        )}
      </div>
    </pre>
  );
};

const generateColumns = ({ scalerAccess, isLidar, isDocumentModel }: ColumnProps) => {
  const baseColumns = [
    {
      accessor: 'createdAt',
      Header: 'Created At',
      width: 100,
      resizeable: true,
      filterable: false,
      sortable: true,
      Cell: TimeFormatter,
    },
    {
      accessor: 'updatedAt',
      Header: 'Updated At',
      width: 100,
      resizeable: true,
      filterable: false,
      sortable: true,
      Cell: TimeFormatter,
    },
    {
      accessor: '_id',
      Header: 'Customer Audit ID',
      width: 250,
      resizeable: true,
      filterable: false,
      sortable: false,
      Cell: CodeFormatter,
    },
    {
      accessor: '_id',
      Header: 'Diff Viewer',
      width: isLidar ? 200 : 120,
      resizeable: true,
      filterable: false,
      sortable: false,
      Cell: ({ value, original }: $FIXME) => diffViewerLinkFormatter(value, isLidar, original),
    },
    {
      accessor: 'reviewedBy',
      Header: 'Reviewed By',
      width: 250,
      resizeable: true,
      filterable: false,
      sortable: false,
      Cell: ({ value }: $FIXME) =>
        ObjectIDFormatter({
          value,
          customUrl: scalerAccess ? `/lookup/${value}` : undefined,
        }),
    },
    {
      accessor: 'active',
      Header: 'Still Active',
      width: 100,
      resizeable: true,
      filterable: false,
      sortable: true,
      Cell: BooleanFormatter,
    },
  ];

  let additionalColumns: any[] = [];

  if (isDocumentModel) {
    const calcAccuracyMetrics = _.memoize(row => calculateQualityScores(row));

    additionalColumns = [
      {
        id: 'num',
        accessor: () => null,
        Header: '#',
        width: 70,
        resizable: true,
        filterable: false,
        sortable: false,
        Cell: ({ index }: { index: number }) => `Audit v${index}`,
      },
      {
        id: 'fscore',
        accessor: (row: $FIXME) => calcAccuracyMetrics(row).f1?.toFixed(3) ?? 'N/A',
        Header: 'F1',
        width: 55,
        resizable: true,
        filterable: false,
        sortable: true,
        Cell: RightAlignedFormatter,
      },
      {
        id: 'errors',
        accessor: (row: $FIXME) => {
          const { errors, critical } = (row.perAnnotationErrors ?? []).reduce(
            (acc: Record<string, number>, { severity }: { severity: string }) => {
              acc.errors += 1;
              if (severity === 'critical') {
                acc.critical += 1;
              }
              return acc;
            },
            { errors: 0, critical: 0 },
          );
          return `${errors}(${critical})`;
        },
        Header: 'Errors(Crit)',
        width: 100,
        resizable: true,
        filterable: false,
        sortable: true,
        Cell: RightAlignedFormatter,
      },
      {
        id: 'precision',
        accessor: (row: $FIXME) => calcAccuracyMetrics(row).precision?.toFixed(3) ?? 0,
        Header: 'Precision',
        width: 80,
        resizable: true,
        filterable: false,
        sortable: true,
        Cell: RightAlignedFormatter,
      },
      {
        id: 'recall',
        accessor: (row: $FIXME) => calcAccuracyMetrics(row).recall?.toFixed(3) ?? 0,
        Header: 'Recall',
        width: 60,
        resizable: true,
        filterable: false,
        sortable: true,
        Cell: RightAlignedFormatter,
      },

      {
        id: 'p',
        accessor: (row: $FIXME) => calcAccuracyMetrics(row).p ?? 0,
        Header: 'P',
        width: 50,
        resizable: true,
        filterable: false,
        sortable: true,
        Cell: RightAlignedFormatter,
      },
      {
        id: 'fixedErrors',
        accessor: ({ perAnnotationErrors = [] }: $FIXME) =>
          perAnnotationErrors.filter((v: { isFixed?: boolean }) => v.isFixed).length,
        Header: 'FP',
        width: 60,
        resizable: true,
        filterable: false,
        sortable: true,
        Cell: RightAlignedFormatter,
      },
    ];
  }

  return [...additionalColumns, ...baseColumns];
};

const CustomerAuditHistory = ({
  audits = [],
  scalerAccess = false,
  isLidar = false,
  isDocumentModel = false,
} = {}) => (
  <>
    <div className="customer-audit-history">
      <Table
        style={{
          width: 'auto',
          height: 'auto',
        }}
        minRows={1}
        showPagination={audits.length > 7}
        columns={generateColumns({
          scalerAccess,
          isLidar,
          isDocumentModel,
        })}
        defaultSorted={
          isDocumentModel
            ? [
                {
                  id: 'num',
                  desc: true,
                },
              ]
            : []
        }
        data={audits}
      />
      <ReactTooltip />
    </div>

    <style jsx>{`
      .ReactTable {
        width: auto;
      }
      .customer-audit-history {
        display: flex;
        flexdirection: column;
      }
    `}</style>
  </>
);

export default CustomerAuditHistory;
